/**
 * SMS auth service — shared/location password sign-in.
 *
 * The shared SimpleJWT adapter (`@aegix/auth-identity`) hard-codes the
 * `POST /rest/sessions` body to `{email, password}`. A shared ("location")
 * role-user login must additionally carry first_name/last_name/cell_phone —
 * `SessionCreate` prechecks role users
 * (`find_missing_fields_for_user_role_login`, 400 without them) and mints
 * the temporary child sub-user from them
 * (`create_new_user_from_existing_user`). This service posts the 5-field
 * body; the caller adopts the child-bound JWT via
 * `useAuthActions().loginWithToken` — the same session-adoption surface the
 * SSO popup path uses. Twin of AIM's `signIn`
 * (`apps/aegix-aim-react/src/main/services/auth.service.ts`);
 * `libs/auth-identity`'s wire contract stays untouched.
 *
 * `/rest/sessions` lives outside `/api/v2/` and therefore outside the
 * drf-spectacular schema — routed through `client.ky` like the other
 * non-schema endpoints (see `licensing.service.ts`).
 */
import { client } from '../lib/http-client';

export interface LocationSignInDetails {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  cell_phone: string;
}

/**
 * SimpleJWT-shaped session response. Only `jwt` is consumed by the caller
 * (the user object is re-fetched via `loginWithToken` → `/me/` as the
 * minted child, so the login response's user fields are ignored here).
 */
export interface LocationSignInResponse {
  jwt?: string;
}

export async function signIn(
  details: LocationSignInDetails,
): Promise<LocationSignInResponse> {
  try {
    const response = await client.ky.post('rest/sessions', { json: details });
    return (await response.json()) as LocationSignInResponse;
  } catch (err) {
    // ky throws HTTPError on non-2xx. Surface the backend's message
    // (`non_field_errors` / `detail`) so the login page's generic
    // `error.message` catch shows the real rejection reason instead of
    // "Request failed with status 400".
    const response = (err as { response?: Response }).response;
    if (response instanceof Response) {
      const body = (await response.json().catch(() => undefined)) as
        { non_field_errors?: string[]; detail?: string } | undefined;
      const message = body?.non_field_errors?.[0] || body?.detail;
      if (message) {
        throw new Error(message);
      }
    }
    throw err;
  }
}
